import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowRight, CheckCircle2, Sliders, FileText, Shield, Star } from 'lucide-react'
import { useConfigStore } from '@/store/config-store'
import { useBuildStore } from '@/store/build-store'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { trackSessionStart } from '@/lib/tracking'

const STEPS = [
  {
    num: '01',
    icon: CheckCircle2,
    label: 'Choose a Model',
    desc: 'Browse our lineup of custom tiny home shells. Each model has its own footprint, sleeping capacity, and starting price.',
  },
  {
    num: '02',
    icon: Sliders,
    label: 'Customize Upgrades',
    desc: 'Mix and match exterior finishes, systems, and off-grid packages. Every selection updates your live estimate instantly.',
  },
  {
    num: '03',
    icon: FileText,
    label: 'Get Your Quote',
    desc: "See a detailed price breakdown before you reach out. We'll follow up to walk you through the build details.",
  },
]

const PROOF = [
  'No commitment required',
  'Real, itemized pricing — not a vague range',
  'Off-grid & financing options available',
  'Built in the Pacific Northwest',
]

export default function Landing() {
  const navigate = useNavigate()
  const { load, loading, error, config } = useConfigStore()
  const { resetBuild, setStep } = useBuildStore()

  useEffect(() => { load() }, [load])
  useEffect(() => { trackSessionStart() }, [])

  const handleStart = () => {
    resetBuild()
    setStep('model')
    navigate('/build/model')
  }

  return (
    <div className="min-h-screen bg-dark-950 text-white flex flex-col">
      <Header variant="plain" />

      <main className="flex-1 flex flex-col">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="flex-1 flex flex-col items-center justify-center px-4 py-20 text-center relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-brand-500/8 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-brand-500/10 border border-brand-500/20 rounded-full px-4 py-1.5 text-brand-400 text-sm font-medium mb-8">
              <Star className="h-3.5 w-3.5 fill-brand-400" />
              Custom tiny homes built to order
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
              Build Your Perfect<br />
              <span className="text-brand-400">Tiny Home</span>
            </h1>

            <p className="text-slate-400 text-xl leading-relaxed mb-4 max-w-xl mx-auto">
              Configure a custom L&M shell online. Choose your model, pick your upgrades, and get
              a real itemized estimate — all before you talk to anyone.
            </p>

            {/* Proof points */}
            <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center mb-10">
              {PROOF.map((p) => (
                <span key={p} className="flex items-center gap-1.5 text-sm text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  {p}
                </span>
              ))}
            </div>

            {/* CTA */}
            {loading && (
              <div className="flex justify-center mb-6">
                <Spinner size="md" />
              </div>
            )}
            {error && (
              <p className="text-red-400 text-sm mb-6">
                Could not load configurator: {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                size="lg"
                onClick={handleStart}
                disabled={loading || !!error || !config}
                loading={loading}
              >
                Start Configuring <ArrowRight className="h-4 w-4" />
              </Button>
              {config && (
                <span className="text-slate-500 text-sm">
                  {config.models.length} models · {config.options.length} upgrades
                </span>
              )}
            </div>
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────────────────── */}
        <section className="border-t border-dark-700 bg-dark-900/50 px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-widest mb-10">
              How it works
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {STEPS.map(({ num, icon: Icon, label, desc }) => (
                <div key={num} className="flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-brand-500/60 font-mono text-xs font-bold">{num}</span>
                    <div className="h-px flex-1 bg-dark-600" />
                  </div>
                  <div className="h-8 w-8 bg-brand-500/10 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="h-4 w-4 text-brand-400" />
                  </div>
                  <div className="font-semibold text-white mb-2">{label}</div>
                  <div className="text-slate-400 text-sm leading-relaxed">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bottom CTA strip ─────────────────────────────────────────────── */}
        <section className="border-t border-dark-700 px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-slate-500" />
              <p className="text-slate-500 text-sm">Estimates are non-binding — no pressure, no spam.</p>
            </div>
            <Button
              size="lg"
              onClick={handleStart}
              disabled={loading || !!error || !config}
              loading={loading}
            >
              Build Your Home <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      </main>

      <footer className="flex justify-center pb-5">
        <Link
          to="/admin/login"
          className="text-slate-600 hover:text-slate-400 text-xs transition-colors"
        >
          Admin Portal
        </Link>
      </footer>
    </div>
  )
}
